import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SentivoLogo } from "./SentivoLogo";

const links = [
  { label: "About", href: "#about" },
  { label: "ElderlyAssist", href: "#elderlyassist" },
  { label: "Focus Areas", href: "#focus" },
  { label: "Impact", href: "#impact" },
  { label: "Team", href: "#team" },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-sentivo-line/60"
          : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo-link">
          <SentivoLogo />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-sentivo-ink/80 hover:text-sentivo-accent transition-colors underline-grow"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            href="#contact"
            data-testid="nav-cta-contact"
            className="inline-flex items-center gap-2 rounded-full bg-sentivo-primary text-[#FAF9F6] px-5 py-2.5 text-sm font-medium hover:bg-sentivo-accent transition-colors duration-300"
          >
            Get in touch
            <span aria-hidden>→</span>
          </a>
        </div>

        <button
          className="md:hidden p-2 rounded-full hover:bg-sentivo-surface transition"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-[#FAF9F6] border-t border-sentivo-line/60"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-base font-medium text-sentivo-ink py-2"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-contact"
                className="mt-2 inline-flex items-center justify-center rounded-full bg-sentivo-primary text-[#FAF9F6] px-5 py-3 text-sm font-medium"
              >
                Get in touch
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
